import type { CSSProperties } from 'react';
import type { FeedEntry } from '~/services/layout';

/** Builds a CSS variable-driven typography style for a configured key prefix. */
export function getTypographyStyle(keyPrefix: string): CSSProperties {
  return {
    fontFamily: `var(--${keyPrefix}_font_family_resolved)`,
    fontSize: `var(--${keyPrefix}_font_size_resolved, 14px)`,
    lineHeight: `var(--${keyPrefix}_line_height_resolved, 18px)`,
    letterSpacing: `var(--${keyPrefix}_letter_spacing_resolved, 0px)`,
    textTransform: 'none',
  };
}

/** Converts an unknown value to number, returning fallback when invalid. */
export function getNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Converts an unknown value to boolean, returning fallback when not boolean. */
export function getBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** Returns true when the value contains non-whitespace text. */
export function hasVisibleText(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/** Clamps a numeric value to the provided min/max bounds. */
export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (value < min) return min;
  if (value > max) return max;

  return value;
}

/**
 * Reads a dotted entry path such as `title` or `extensions.original_album_name`.
 */
function readEntryPath(entry: FeedEntry, key: string): unknown {
  return key.split('.').reduce<unknown>((current, segment) => {
    if (!current || typeof current !== 'object') return undefined;

    return (current as Record<string, unknown>)[segment];
  }, entry);
}

/** Resolves text by walking the layout data key as a path on the entry. */
export function resolveEntryText(
  entry: FeedEntry | null,
  key: unknown,
  fallback?: string
): string {
  if (!entry) return fallback || '';

  if (typeof key === 'string' && key.length > 0) {
    const value = readEntryPath(entry, key);

    if (typeof value === 'string' && value.length > 0) return value;
  }

  return fallback || '';
}

/** Resolves an image source from media_group, preferring a configured media key. */
export function getAudioImageSrc(
  entry: FeedEntry | null,
  preferredKey: unknown
): string | undefined {
  const mediaItems = entry?.media_group?.find(
    (mediaGroup) => mediaGroup?.type === 'image'
  )?.media_item;

  if (!Array.isArray(mediaItems)) return undefined;

  if (typeof preferredKey === 'string' && preferredKey.length > 0) {
    const preferredImage = mediaItems.find(
      (mediaItem) => mediaItem?.key === preferredKey
    )?.src;

    if (preferredImage) return preferredImage;
  }

  return (
    mediaItems.find((mediaItem) => mediaItem?.key === 'image_base')?.src ||
    mediaItems.find((mediaItem) => mediaItem?.src)?.src
  );
}

/** Infers audio MIME type from source URL with fallback to entry content type. */
export function resolveAudioMimeType(entry: FeedEntry): string {
  const src = entry.content?.src || '';

  if (src.includes('.m3u8')) return 'application/x-mpegURL';
  if (src.includes('.mp3')) return 'audio/mpeg';

  return entry.content?.type || 'audio/mpeg';
}
